import { connectNestConnection, createNestClient, type NestClient } from "../nest/client"
import type { NestConnection } from "../nest/routes"
import { requestAppOperation, readAgentAccess, setAgentAccess } from "../wildlark/appClient"
import { requestWildLarkMethod } from "../wildlark/client"
import { parseTaskRecord } from "../persistence/records"
import { installControlTools } from "../appControls/tools"
import { installComputerAgentTools } from "../computers/agentTools"
import { showPermissionPrompt } from "../components/permissions/PermissionPromptModal"
import { voiceManager } from "./manager"
import type { PermissionMode } from "../nest/generated"
import type { PermissionPromptResponse, PermissionRequestNotification } from "../nest/client"
import type { CodeStore } from "../store/store"
import { readVoiceBriefing, type VoiceBriefing } from "./briefing"
import { voiceStartupContext } from "./orientation"
import { readVoiceResponse } from "./messages"
import type { HarnessId } from "../providerEvents/harnessEventTypes"

// Both sockets use the same presentation policy, so arrival order cannot decide
// whether the controller's permission dialog supports spoken confirmation.
export function showControllerPermissionPrompt(request: PermissionRequestNotification, nodeId: string | null): Promise<PermissionPromptResponse> {
    if (!nodeId) return Promise.resolve({ decision: "deny", message: "The permission request has no connected computer" })
    return showPermissionPrompt(request, {
        nodeId,
        signal: voiceManager.permissionSignal(nodeId, request.sessionId),
        registerDecision: (settle) => {
            if (voiceManager.snapshot().controller?.nodeId !== nodeId) return () => {}
            return voiceManager.requestConfirmation(
                request.sessionId,
                request.kind === "command" ? "command" : "operation",
                (request.title ?? "Approval required") + ": " + (request.command ?? request.description ?? request.toolName ?? request.kind),
                (accepted) => settle({ decision: accepted ? "allow" : "deny" })
            )
        },
    })
}

export interface ControllerConfig {
    connection: NestConnection
    workspaceId: string
    selectedTaskId?: string
    harnessId: HarnessId
    model: string
    thinking: string
    permissionMode: PermissionMode
}
export class VoiceController {
    private tail: Promise<unknown> = Promise.resolve()
    private closing = false
    private initialized = false
    private constructor(
        readonly client: NestClient,
        readonly taskId: string,
        readonly config: ControllerConfig,
        readonly briefing: VoiceBriefing,
        private disposers: Array<() => void>
    ) {}

    static async create(config: ControllerConfig, store: CodeStore): Promise<VoiceController> {
        const client = createNestClient()
        const disposers = [
            installControlTools(client),
            installComputerAgentTools(client),
            client.registerPermissionHandler((request) => showControllerPermissionPrompt(request, config.connection.nodeId)),
        ]
        try {
            await connectNestConnection(client, config.connection, { timeoutMs: 6000 })
            const created = await requestAppOperation(
                "task.create",
                {
                    workspaceId: config.workspaceId,
                    description: "Global voice control",
                    harnessId: config.harnessId,
                    model: config.model,
                    thinking: config.thinking,
                    clientRequestId: crypto.randomUUID(),
                },
                undefined,
                client
            )
            const record = parseTaskRecord(created.task)
            if (!record) throw new Error("Could not create the voice Admin task")
            const access = await readAgentAccess(record.id, undefined, client)
            if (!access.available.some((module) => module.id === "interface") || !access.available.some((module) => module.id === "actions")) {
                throw new Error("Update this computer's Nest to enable voice client controls")
            }
            await setAgentAccess(
                {
                    taskId: record.id,
                    sessionId: record.id,
                    workspaceId: config.workspaceId,
                    modules: access.available.map((module) => module.id),
                    role: "chief-of-staff",
                    expectedVersion: access.version,
                },
                undefined,
                client
            )
            const briefing = await readVoiceBriefing(client, store, {
                nodeId: config.connection.nodeId,
                workspaceId: config.workspaceId,
                selectedTaskId: config.selectedTaskId,
                controllerTaskId: record.id,
            })
            return new VoiceController(client, record.id, config, briefing, disposers)
        } catch (error) {
            for (const dispose of disposers) dispose()
            client.close()
            throw error
        }
    }

    run(input: string, requestId: string): Promise<string> {
        if (this.closing) return Promise.reject(new Error("The voice controller is closing"))
        // Keep native-history reads tied to one completed turn. No queued successor
        // can race the bounded final-message read.
        const work = this.tail.then(async () => {
            const started = await requestWildLarkMethod(
                "wildlark.task.turn.start",
                {
                    taskId: this.taskId,
                    workspaceId: this.config.workspaceId,
                    sessionId: this.taskId,
                    input: {
                        parts: [
                            ...(!this.initialized
                                ? [
                                      {
                                          type: "text" as const,
                                          text: voiceStartupContext(this.briefing).join("\n"),
                                      },
                                  ]
                                : []),
                            { type: "text", text: input },
                        ],
                    },
                    disposition: "enqueue",
                    clientRequestId: requestId,
                    ...(!this.initialized ? { prompt: { voiceController: true } } : {}),
                    options: {
                        mode: "full",
                        permissionMode: this.config.permissionMode,
                        model: this.config.model,
                        providerOptions: { thinking: this.config.thinking },
                    },
                },
                undefined,
                this.client
            )
            // Point-read authoritative runtime state, never rescan accumulated history.
            const deadline = Date.now() + 30 * 60_000
            for (;;) {
                const { turn } = await this.client.requestMethod("session.turn.read", { turnId: started.turn.turnId })
                if (turn.completedAt || ["completed", "failed", "cancelled", "interrupted", "error"].includes(turn.status)) {
                    if (turn.error) throw new Error(turn.error)
                    if (turn.status !== "completed") return `Controller turn ended with status: ${turn.status}. Check the voice Admin task before retrying.`
                    // Confirm native execution before omitting birth instructions.
                    // A failed first attempt must still be able to initialize on retry.
                    this.initialized = true
                    return (
                        (await readVoiceResponse(this.client, { sessionId: this.taskId, harnessId: this.config.harnessId })) ??
                        "The controller finished without a spoken result. Open its task to inspect the outcome."
                    )
                }
                if (Date.now() >= deadline)
                    throw new Error("The controller is still running. Open its task to check progress; its work has not been cancelled.")
                await new Promise<void>((resolve) => setTimeout(resolve, 1500))
            }
        })
        this.tail = work.catch(() => {})
        return work
    }

    closeWhenIdle(): void {
        if (this.closing) return
        this.closing = true
        void this.tail.finally(() => {
            for (const dispose of this.disposers) dispose()
            this.disposers = []
            this.client.close()
        })
    }
}
